import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Badge, BadgeStatus } from '@/components/Badge/Badge';
import { Verdict } from '@/helpers/ratingState';
import { CardSectionWideProps } from './CardSectionWide.types';
import bgNeutral from './bg-neutral.png';
import bgOk from './bg-ok.png';
import { BadgeStatusType } from '../Badge/BadgeTypes';

export const CardSectionWide: React.FC<CardSectionWideProps> = ({
  title,
  description,
  verdict,
  badgeCustomText,
  className,
}) => {
  const status = useMemo(() => {
    if (verdict === Verdict.NEGATIVE) return BadgeStatusType.Error;
    if (verdict === Verdict.NEUTRAL) return BadgeStatusType.Neutral;
    if (verdict === Verdict.POSITIVE) return BadgeStatusType.Success;
    return BadgeStatusType.Neutral;
  }, [verdict]);

  return (
    <div
      className={clsx(
        'relative w-full overflow-hidden rounded-3xl border border-solid shadow-2xl' /* max-w-[836px] */,
        className,
        {
          'border-emerald-300': verdict === Verdict.POSITIVE,
          'border-red-300': verdict === Verdict.NEGATIVE,
          'border-gray-300': verdict === Verdict.NEUTRAL,
        },
      )}
    >
      <div
        className="absolute inset-0 bottom-0 right-0 bg-no-repeat"
        style={{
          backgroundImage: `url(${verdict === Verdict.NEUTRAL ? bgNeutral : bgOk
            })`,
          backgroundSize: '75%',
          backgroundPosition: '100% 100%',
        }}
      ></div>
      <div className="relative flex w-full flex-wrap items-center gap-6 px-6 pb-5 pt-6 max-md:max-w-full max-md:px-5">
        <div className="my-auto flex flex-col self-stretch">
          <div className="flex flex-col items-start font-bold">
            <div className="text-xl leading-snug text-zinc-800">{title}</div>
            <div className="mt-2">
              <Badge variant={status} badgeText={badgeCustomText} version='v2' />
            </div>
          </div>
          <div className="mt-6 flex min-h-[76px] items-start gap-5">
            <div className="flex h-[54px] w-[54px] items-center justify-center">
              {verdict === Verdict.POSITIVE ? (
                <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle opacity="0.5" cx="27" cy="27" r="27" fill="url(#paint0_radial_1275_12598)" />
                  <circle cx="27.0008" cy="26.9939" r="21.7742" fill="#148B4D" />
                  <path d="M22.8034 36.5786L15.6792 29.5338L18.6729 26.5727L22.8034 30.6574L37.0726 16.5464L40.0663 19.5072L22.8034 36.5786Z" fill="white" />
                  <defs>
                    <radialGradient id="paint0_radial_1275_12598" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(27 27) rotate(90) scale(27)">
                      <stop offset="0.495225" stop-color="#111438" stop-opacity="0" />
                      <stop offset="1" stop-color="#148B4D" />
                    </radialGradient>
                  </defs>
                </svg>
              ) : (
                <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle opacity="0.5" cx="27" cy="27" r="27" fill="url(#paint0_radial_1275_12613)" />
                  <circle cx="27.0008" cy="26.9939" r="21.7742" fill="#E7EDF6" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M15.677 35.575L35.7092 15.5427L38.7886 18.6221L18.7563 38.6543L15.677 35.575Z" fill="#A4B2C0" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M35.7088 38.6551L15.6765 18.6228L18.7559 15.5435L38.7881 35.5757L35.7088 38.6551Z" fill="#A4B2C0" />
                  <defs>
                    <radialGradient id="paint0_radial_1275_12613" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(27 27) rotate(90) scale(27)">
                      <stop offset="0.495225" stop-color="white" />
                      <stop offset="1" stop-color="#DEE7F4" />
                    </radialGradient>
                  </defs>
                </svg>
              )}
            </div>
            <div className="relative z-[2] mt-0 max-w-[80%] self-stretch text-left text-base font-medium leading-7 text-[#334931] max-md:max-w-full">
              {description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSectionWide;
